<template>
  <div>
    <h4 class="mb-3 pb-3 border-bottom d-flex">
      <span>
        {{ $t('agents.commissionsList') }}
      </span>

      <d-button
        v-if="hasSelectedPayOuts"
        theme="success"
        @click.prevent="payOut"
        class="ml-auto">
        <div
          v-if="payoutInProgress"
          class="spinner-border spinner-border-sm"
          role="status"></div>
        {{ $t('agents.commissions.payOut') }}
      </d-button>
    </h4>

    <list-filters
      class="mb-3"
      :config="filtersConfig"
      :isListLoaded="allDataLoaded"
      :view-name="'agent-commissions'"
      :disableStorage="true"
      @init="handleFiltersInit"
      @search="handleFiltersSearch" />

    <d-container
      fluid
      class="main-content-container px-0">
      <d-card style="width: 100%;">
        <table class="table mt-0 mb-0 full-width">
          <thead class="bg-light">
            <tr>
              <th
                scope="col"
                class="border-0 pr-0">
                <d-checkbox
                  class="mb-0 pr-0"
                  v-model="selectAll"
                  @change="toggleItems" />
              </th>
              <th
                scope="col"
                class="border-0">{{ $t('agents.commissions.orderName') }}</th>
              <th
                scope="col"
                class="border-0">{{ $t('agents.commissions.orderStatus') }}</th>
              <th
                scope="col"
                class="border-0">{{ $t('agents.commissions.orderDate') }}</th>
              <th
                scope="col"
                class="border-0">{{ $t('agents.commissions.invoiceExists') }}</th>
              <th
                scope="col"
                class="border-0">{{ $t('agents.commissions.paymentStatus') }}</th>
              <th
                scope="col"
                class="border-0">{{ $t('agents.commissions.commission') }}</th>
            </tr>
          </thead>
          <tbody v-if="allDataLoaded">
            <tr
              v-for="(commission, index) of commissions"
              :key="'commission-' + index">
              <td
                class="align-middle pr-0">
                <d-checkbox
                  v-if="commission.can_be_settled"
                  class="mb-0 pr-0"
                  v-model="selectedItems[commission.id]" />
              </td>
              <td class="align-middle">
                <a
                  href="#"
                  @click.prevent="openOrderInNewTab(commission.order.id)">
                  {{ commission.order.order_name }}
                </a>
              </td>
              <td class="align-middle">
                <status-badge
                  :status="commission.order.status" />
              </td>
              <td
                class="align-middle"
                v-pure-html="commission.order.created_at.split(' ').join('<br>')">
              </td>
              <td class="align-middle">
                <span
                  v-if="commission.settlement_id"
                  class="text-success">
                  {{ $t('ui.yes') }}
                </span>
                <span
                  v-else
                  class="text-daner">
                  {{ $t('ui.no') }}
                </span>

                <small
                  v-if="commission.settlement_id && commission.settlement"
                  class="d-block">
                  ({{ commission.settlement.amount_net }} {{ commission.settlement.currency }})
                </small>
              </td>
              <td class="align-middle">
                <template v-if="commission.settlement_id && commission.settlement">
                  <d-badge
                    v-if="commission.settlement.status === 'PAID'"
                    theme="success">
                    {{ $t('agents.commissions.settlementStatus.PAID') }}
                  </d-badge>
                  <d-badge
                    v-if="commission.settlement.status === 'UNPAID'"
                    theme="danger">
                    {{ $t('agents.commissions.settlementStatus.UNPAID') }}
                  </d-badge>
                </template>
              </td>
              <td class="align-middle">
                <span
                  v-if="commission.settlement && commission.settlement.status === 'PAID'"
                  class="text-success">
                  {{ commission.commission_amount }} {{ commission.currency_code }}
                </span>
                <span
                  v-else-if="commission.settlement && commission.settlement.status === 'UNPAID'"
                  class="text-danger">
                  {{ commission.commission_amount }} {{ commission.currency_code }}
                </span>
                <span v-else>
                  {{ commission.commission_amount }} {{ commission.currency_code }}
                </span>
                <br>
                <small>
                  {{ $t('agents.commissions.comissionStatus.' + commission.agent_settlement_status) }}
                </small>
              </td>
            </tr>
            <tr v-if="commissions.length === 0">
              <td
                colspan="7"
                class="text-center">
                {{ $t('agents.commissions.noCommissionsToDisplay') }}&hellip;
              </td>
            </tr>
          </tbody>
        </table>

        <div
          v-if="!allDataLoaded"
          class="pb-3 pt-3 text-center">
          {{ $t('ui.dataLoading') }}&hellip;
        </div>

        <d-alert
          v-if="loadError"
          show
          theme="warning">
          {{ $t('ui.dataLoadingFailed') }} <a
            href="javascript:window.location.reload();"
          class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
        </d-alert>
      </d-card>

      <pagination
        :meta-data="paginationMetaData"
        :initial-page="currentPage" />
    </d-container>
  </div>
</template>

<script>
import debounce from 'lodash.debounce';
import cloneDeep from 'lodash.clonedeep';
import CalendarUtils from '@/utils/CalendarUtils.js';
import FormUtils from '@/utils/FormUtils.js';
import HasPagination from '@/mixins/HasPagination.vue';
import ListUtils from '@/utils/ListUtils.js';
import Pagination from '@/components/common/Pagination.vue';
import StatusBadge from '@/components/common/StatusBadge.vue';
import Vue from 'vue';

export default {
  name: 'agent-commissions',
  mixins: [
    HasPagination
  ],
  props: [
    'agentID'
  ],
  components: {
    'pagination': Pagination,
    'status-badge': StatusBadge
  },
  computed: {
    allDataLoaded () {
      return this.loaded && this.loadedCurrencies;
    },
    currencyOptions () {
      return this.currencies.map(currency => ({
        value: currency.id,
        text: currency.code
      }));
    },
    hasSelectedPayOuts () {
      let commissionsToPayOut = Object.keys(this.selectedItems);
      let hasAny = false;

      for (let i = 0; i < commissionsToPayOut.length; i++) {
        if (this.selectedItems[commissionsToPayOut[i]]) {
          hasAny = true;
        }
      }

      return hasAny;
    },
    selectedToPayOut () {
      let commissionsToPayOut = Object.keys(this.selectedItems);
      let toPayOut = [];

      for (let i = 0; i < commissionsToPayOut.length; i++) {
        if (this.selectedItems[commissionsToPayOut[i]]) {
          toPayOut.push(parseInt(commissionsToPayOut[i], 10));
        }
      }

      return toPayOut;
    },
    orderStatusOptions () {
      return [
        {
          text: this.$t('order.toCollect'),
          value: 'TO_COLLECT'
        },
        {
          text: this.$t('order.waitingPurchaseProducts'),
          value: 'WAITING_PURCHASE_PRODUCTS'
        },
        {
          text: this.$t('ui.order'),
          value: 'ORDER'
        },
        {
          text: this.$t('order.inProduction'),
          value: 'IN_PRODUCTION'
        },
        {
          text: this.$t('order.toCheckInWarehouse'),
          value: 'TO_CHECK'
        },
        {
          text: this.$t('order.readyForShipping'),
          value: 'PREPARATION_FOR_SHIPPING'
        },
        {
          text: this.$t('order.waitingForShipping'),
          value: 'WAITING_FOR_SHIPPING'
        },
        {
          text: this.$t('order.sent'),
          value: 'SENT'
        },
        {
          text: this.$t('order.pendingPayment'),
          value: 'PENDING_PAYMENT'
        },
        {
          text: this.$t('order.ended'),
          value: 'ENDED'
        },
        {
          text: this.$t('order.toDelete'),
          value: 'TO_DELETE'
        },
        {
          text: this.$t('order.deleted'),
          value: 'DELETED'
        }
      ];
    },
    selectedCommissions () {
      return Object.keys(this.selectedItems).filter(item => !!this.selectedItems[item]).map(item => parseInt(item, 10)).sort();
    },
    visibleCommissions () {
      return this.commissions.filter(commission => commission.can_be_settled).map(commission => commission.id).sort();
    },
    filtersConfig () {
      return [
        {
          key: 'order_name',
          label: this.$t('ui.orderName'),
          placeholder: this.$t('ui.orderName'),
          type: 'text',
          isBasic: true,
          defaultValue: '',
          apiTransform: (val, where) => {
            if (!where.order) {
              where.order = {};
            }
            where.order.name = val;
          }
        },
        {
          key: 'orderDateRange',
          label: this.$t('agents.commissions.orderDate'),
          placeholder: this.$t('agents.commissions.orderDate'),
          type: 'daterange',
          isBasic: true,
          defaultValue: { startDate: null, endDate: null },
          apiTransform: (val, where) => {
            if (!where.order) {
              where.order = {};
            }
            if (val.startDate) {
              where.order.created_at_from = this.formatDate(val.startDate);
            }
            if (val.endDate) {
              where.order.created_at_to = this.formatDate(val.endDate);
            }
          }
        },
        {
          key: 'order_status',
          label: this.$t('agents.commissions.orderStatus'),
          placeholder: this.$t('status.statusQuestionMark'),
          type: 'select',
          isBasic: true,
          defaultValue: null,
          options: this.orderStatusOptions,
          apiTransform: (val, where) => {
            if (!where.order) {
              where.order = {};
            }
            where.order.status = val;
          }
        },
        {
          key: 'status',
          label: this.$t('agents.commissions.commissionStatusOption'),
          placeholder: this.$t('agents.commissions.commissionStatusOption'),
          type: 'select',
          isBasic: true,
          defaultValue: null,
          options: [
            { text: this.$t('agents.commissions.comissionStatus.PAID'), value: 'PAID' },
            { text: this.$t('agents.commissions.comissionStatus.TO_PAY'), value: 'TO_PAY' }
          ]
        },
        {
          key: 'currency_id',
          label: this.$t('settings.currency.chooseCurrency'),
          placeholder: this.$t('settings.currency.chooseCurrency'),
          type: 'select',
          isBasic: true,
          defaultValue: null,
          options: this.currencyOptions
        },
        {
          key: 'settlement_number',
          label: this.$t('agents.commissions.settlementNumber'),
          placeholder: this.$t('agents.commissions.settlementNumber'),
          type: 'text',
          isBasic: false,
          defaultValue: '',
          apiTransform: (val, where) => {
            if (!where.settlement) {
              where.settlement = {};
            }
            where.settlement.number = val;
          }
        },
        {
          key: 'invoiceDateRange',
          label: this.$t('ui.invoiceDate'),
          placeholder: this.$t('ui.invoiceDate'),
          type: 'daterange',
          isBasic: false,
          defaultValue: { startDate: null, endDate: null },
          apiTransform: (val, where) => {
            if (!where.settlement) {
              where.settlement = {};
            }
            if (val.startDate) {
              where.settlement.invoice_date_from = this.formatDate(val.startDate);
            }
            if (val.endDate) {
              where.settlement.invoice_date_to = this.formatDate(val.endDate);
            }
          }
        },
        {
          key: 'settlement_status',
          label: this.$t('agents.commissions.settlementStatusOption'),
          placeholder: this.$t('agents.commissions.settlementStatusOption'),
          type: 'select',
          isBasic: false,
          defaultValue: null,
          options: [
            { text: this.$t('agents.commissions.settlementStatus.PAID'), value: 'PAID' },
            { text: this.$t('agents.commissions.settlementStatus.UNPAID'), value: 'UNPAID' }
          ]
        }
      ];
    }
  },
  data () {
    return {
      commissions: [],
      currencies: [],
      loaded: false,
      loadError: false,
      loadedCurrencies: false,
      removeInProgress: false,
      selectedItems: {},
      payoutInProgress: false,
      selectAll: false,
      activeFilters: {},
      apiQueryPayload: {}
    };
  },
  watch: {
    'selectedCommissions': {
      handler () {
        if (this.visibleCommissions.length && this.selectedCommissions.join('-') === this.visibleCommissions.join('-')) {
          this.selectAll = true;
        } else if (this.selectedCommissions.length) {
          this.selectAll = true;
        } else {
          this.selectAll = false;
        }
      }
    }
  },
  mounted () {
    this.loadAdditionalData();
    this.$bus.$on('reload-agent-commissions-list', this.debouncedLoadFilteredData);
  },
  methods: {
    handleFiltersInit (filters, apiPayload) {
      this.activeFilters = filters;
      this.apiQueryPayload = apiPayload;

      this.loadFilteredData();
    },
    handleFiltersSearch (activeFilters, apiPayload) {
      this.activeFilters = activeFilters;
      this.apiQueryPayload = apiPayload;
      this.$bus.$emit('pagination-reset');
      this.debouncedLoadFilteredData();
    },
    debouncedLoadFilteredData: debounce(function () {
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      Vue.set(this, 'selectedItems', {});

      let whereData = cloneDeep(this.apiQueryPayload) || {};
      whereData.agent_id = this.agentID;

      ListUtils.loadPaginatedItemsData(this, {
        endpoint: '/agents/commissions/items?page=' + this.currentPage,
        listField: 'commissions',
        params: {
          where: whereData
        }
      });
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/currencies/items',
        loadedKey: 'loadedCurrencies',
        outputKey: 'currencies',
        errorKey: 'loadError'
      });
    },
    payOut () {
      this.payoutInProgress = true;

      FormUtils.submitInBackground({
        method: 'post',
        $http: this.$http,
        endpoint: '/agents/commissions/store',
        formData: {
          items: this.selectedToPayOut
        },
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('agents.commissions.errorWhenPayOut'),
        successAction: () => {
          this.payoutInProgress = false;
          this.loadFilteredData();
          this.$bus.$emit('reload-agent-settlements-list');
        },
        failAction: () => {
          this.payoutInProgress = false;
        }
      });
    },
    openOrderInNewTab (orderID) {
      window.open(this.$router.resolve('/zamowienia/edycja/' + orderID).href, '_blank');
    },
    toggleItems () {
      Vue.nextTick(() => {
        if (this.selectAll) {
          for (let i = 0; i < this.visibleCommissions.length; i++) {
            let id = this.visibleCommissions[i];
            Vue.set(this.selectedItems, id, true);
          }
        } else {
          Vue.set(this, 'selectedItems', []);
        }
      });
    },
    formatDate (date) {
      return CalendarUtils.formatDateForPHP(new Date(date));
    }
  },
  beforeDestroy () {
    this.$bus.$off('reload-agent-commissions-list', this.debouncedLoadFilteredData);
  }
};
</script>
